import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import isInteger from 'lodash/isInteger';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Assert that the given collection is explicitly an Array object.
 * @param {*} collection
 * @returns {boolean} Returns true if the collection is an Array object.
 * @throws {TypeError} Throws error if the collection is not an Array object.
 */
export function assertCollectionIsArray(collection) {
    if (! isArray(collection)) {
        throw new TypeError('Collection is not an Array.');
    }

    return true;
}

/**
 * Assert that the given collection is a valid CMS collection type.
 * @param {*} collection
 * @returns {boolean} Returns true if the collection is a valid CMS collection type.
 * @throws {TypeError} Throws error if the collection is not a valid CMS collection type.
 */
export function assertCollectionIsValid(collection) {
    assertCollectionIsArray(collection);

    try {
        collection.every(assertItemIsValid);
    } catch (thrown) {
        throw new TypeError('Collection type is not valid.');
    }

    return true;
}

/**
 * Assert that the given item is explicitly a plain object.
 * @param {*} item
 * @returns {boolean} Returns true if the item is a plain object.
 * @throws {TypeError} Throws error if the item is not a plain object.
 */
export function assertItemIsObject(item) {
    if (! isPlainObject(item)) {
        throw new TypeError('Item is not an Object.');
    }

    return true;
}

/**
 * Assert that the given item is a valid CMS item type.
 * @param {*} item
 * @returns {boolean} Returns true if the item is a valid CMS item type.
 * @throws {TypeError} Throws error if the item is not a valid CMS item type.
 */
export function assertItemIsValid(item) {
    assertItemIsObject(item);

    if (! isInteger(item?.id)
        || ! isPlainObject(item?.attributes)
        || ! item.attributes?.createdAt
        || ! item.attributes?.updatedAt
    ) {
        throw new TypeError('Item is not valid.');
    }

    return true;
}

/**
 * Get the URL path for a given CMS media item.
 * @param {*} media
 * @returns {string} Returns the media's url.
 * @throws {TypeError} Throws error if the media is not a valid CMS item type.
 */
export function getMediaPathUrl(media) {
    let mediaObject = cloneDeep(media);

    if (isPlainObject(mediaObject) && mediaObject?.data != null) {
        // Ascertain that the media data is an object, using that as the mediaObject.
        assertItemIsObject(mediaObject.data);
        mediaObject = mediaObject.data;
    }

    // Ascertain that the mediaObject is a valid CMS item type.
    assertItemIsValid(mediaObject);

    // Return the full path to the media asset from the CMS service.
    const basePath = process.env.VUE_APP_CMS_BASE_URL ?? 'https://cms.edurent.com';
    const mediaPath = mediaObject.attributes.url;

    if (! basePath || ! mediaPath) {
        throw new TypeError('The Media is not a valid CMS media type.');
    }

    return `${basePath}${mediaPath}`;
}

/**
 * Map a CMS collection into a list of flattened items.
 * @param {*} collection
 * @returns {Object[]} Returns an array of items.
 * @throws {TypeError} Throws error if the collection is not a valid CMS collection type.
 */
export function mapCollectionFromCMS(collection) {
    let collectionArray = cloneDeep(collection);

    if (isPlainObject(collectionArray)) {
        // Ascertain that the collection data is an array, using that as the collectionArray.
        assertCollectionIsArray(collectionArray.data);
        collectionArray = collectionArray.data;
    }

    // Ascertain that the collectionArray is a valid CMS collection type.
    assertCollectionIsValid(collectionArray);

    // Map the valid collectionArray into a flattened collection.
    return collectionArray.map((item) => {
        return Object.assign({}, { id: item.id }, item.attributes);
    });
}

/**
 * Add the Stripe percentage fee (2.9%),
 * the Stripe absolute charge (30 cents),
 * and the business percentage fee (7%)
 * together to calculate the total
 * destination fee to be paid by the donor.
 *
 * @param donationAmount
 * @returns {number}
 */
export function calculateDestinationFee(donationAmount) {
    let stripePercentage = 0.029; // 2.9% Stripe Fee
    let stripeConstant = 0.3; // 30 cent Stripe Fee
    let businessFee = .07; // 7% business fee

    let destinationFeeFormula = (donationAmount * stripePercentage)
        + (donationAmount * businessFee)
        + stripeConstant;

    return Number(
        destinationFeeFormula
    );
}
